import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Button, Spinner, Table, Badge } from 'reactstrap';
import usePermission from '../Hooks/usePermission';
import { fetchAssetCost, clearAssetCost } from '../store/assetCost/actions';
import { formatCurrency } from '../helpers/formatCurrency';

/**
 * Admin-only cumulative maintenance cost for a single asset (and optionally descendants),
 * over a configurable period. Drop into the Asset detail page.
 */

const PRESETS = ['THIS_MONTH', 'QUARTER', 'YEAR', 'LAST_12_MONTHS', 'ALL_TIME', 'CUSTOM'];

const isoDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const rangeFor = (preset, customFrom, customTo) => {
  const now = new Date();
  switch (preset) {
    case 'THIS_MONTH': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: isoDate(from), to: isoDate(to) };
    }
    case 'QUARTER': {
      const q = Math.floor(now.getMonth() / 3);
      const from = new Date(now.getFullYear(), q * 3, 1);
      const to = new Date(now.getFullYear(), q * 3 + 3, 0);
      return { from: isoDate(from), to: isoDate(to) };
    }
    case 'YEAR': {
      const from = new Date(now.getFullYear(), 0, 1);
      const to = new Date(now.getFullYear(), 11, 31);
      return { from: isoDate(from), to: isoDate(to) };
    }
    case 'LAST_12_MONTHS': {
      const from = new Date(now);
      from.setFullYear(from.getFullYear() - 1);
      return { from: isoDate(from), to: isoDate(now) };
    }
    case 'CUSTOM':
      return { from: customFrom || null, to: customTo || null };
    case 'ALL_TIME':
    default:
      return { from: null, to: null };
  }
};

const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isNaN(n) ? 0 : n;
};

const AssetCostRollupCard = ({ assetId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const cost = useSelector((s) => s.AssetCost?.cost);
  const loading = useSelector((s) => s.AssetCost?.loading);
  const error = useSelector((s) => s.AssetCost?.error);

  const canRead = hasPermission('cost:read-asset-rollup');

  const [preset, setPreset] = useState('YEAR');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [includeDowntime, setIncludeDowntime] = useState(true);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const range = useMemo(() => rangeFor(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const displayedTotal = useMemo(() => {
    if (!cost) return null;
    if (includeDowntime) return cost.totalCost;
    return toNum(cost.totalCost) - toNum(cost.downtimeCost);
  }, [cost, includeDowntime]);

  useEffect(() => {
    if (!canRead || !assetId) return;
    dispatch(fetchAssetCost({
      assetId,
      from: range.from,
      to: range.to,
      includeChildren,
    }));
    return () => dispatch(clearAssetCost());
  }, [dispatch, canRead, assetId, range.from, range.to, includeChildren]);

  if (!canRead) return null;

  return (
    <Card className="border-0 shadow-sm mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap" style={{ gap: '8px' }}>
          <div>
            <h5 className="mb-1 d-flex align-items-center" style={{ gap: '8px' }}>
              {t('cost_tracking.asset_rollup.title', 'Maintenance cost')}
              {/* Suppressed when includeChildren is on — replacement is a per-asset signal. */}
              {cost?.replacementSuggested && !includeChildren && (
                <Badge color="danger" pill>
                  <i className="bx bx-error-circle me-1"></i>
                  {t('cost_tracking.asset_tco.replacement_suggested', 'Replacement Suggested')}
                </Badge>
              )}
            </h5>
            <small className="text-muted">
              {t('cost_tracking.asset_rollup.subtitle', 'Cumulative cost on this asset' + (includeChildren ? ' and child assets' : ''))}
            </small>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '16px' }}>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="rollupIncludeDowntimeSwitch"
                checked={includeDowntime}
                onChange={(e) => setIncludeDowntime(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="rollupIncludeDowntimeSwitch">
                {t('cost_tracking.asset_tco.include_downtime', 'Include downtime cost')}
              </label>
            </div>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="includeChildrenSwitch"
                checked={includeChildren}
                onChange={(e) => setIncludeChildren(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="includeChildrenSwitch">
                {t('cost_tracking.asset_rollup.include_children', 'Include child assets')}
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap mb-3" style={{ gap: '6px' }}>
          {PRESETS.map((p) => (
            <Button
              key={p}
              size="sm"
              color={preset === p ? 'primary' : 'light'}
              outline={preset !== p}
              onClick={() => setPreset(p)}
            >
              {t(`cost_tracking.asset_rollup.preset.${p}`, p)}
            </Button>
          ))}
        </div>

        {preset === 'CUSTOM' && (
          <div className="d-flex flex-wrap mb-3" style={{ gap: '8px' }}>
            <div>
              <label className="form-label small mb-1">
                {t('cost_tracking.asset_rollup.from', 'From')}
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label small mb-1">
                {t('cost_tracking.asset_rollup.to', 'To')}
              </label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-3">
            <Spinner size="sm" /> {t('cost_tracking.asset_rollup.loading', 'Loading…')}
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}
        {!loading && cost && (
          <>
            {cost.hasMissingPriceData && (
              <div className="alert alert-warning small">
                {t('cost_tracking.asset_rollup.missing_price_data',
                  'Some consumed parts have no unit price set — total may be understated.')}
              </div>
            )}
            <Table size="sm" borderless className="mb-3">
              <tbody>
                <tr>
                  <td>{t('cost_tracking.wo_total.labor', 'Labor')}</td>
                  <td className="text-end">{formatCurrency(cost.laborCost, cost.currency)}</td>
                </tr>
                <tr>
                  <td>{t('cost_tracking.wo_total.parts', 'Parts')}</td>
                  <td className="text-end">{formatCurrency(cost.partsCost, cost.currency)}</td>
                </tr>
                <tr>
                  <td>{t('cost_tracking.wo_total.contractor', 'Contractor')}</td>
                  <td className="text-end">{formatCurrency(cost.contractorCost, cost.currency)}</td>
                </tr>
                <tr className={includeDowntime ? '' : 'text-muted'} style={!includeDowntime ? { textDecoration: 'line-through' } : undefined}>
                  <td>{t('cost_tracking.wo_total.downtime', 'Downtime')}</td>
                  <td className="text-end">{formatCurrency(cost.downtimeCost, cost.currency)}</td>
                </tr>
                <tr className="fw-bold border-top">
                  <td>{t('cost_tracking.wo_total.total', 'Total')}</td>
                  <td className="text-end">{formatCurrency(displayedTotal, cost.currency)}</td>
                </tr>
              </tbody>
            </Table>
            <div className="text-muted small">
              {t('cost_tracking.asset_rollup.scope', '{{wo}} work order(s) across {{assets}} asset(s)', {
                wo: cost.workOrdersInScope ?? 0,
                assets: cost.assetsInScope ?? 1,
              })}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default AssetCostRollupCard;
